import React, { useMemo, useState } from 'react'
import { useLocation, useHistory } from 'react-router-dom'
import { useGenericQuery } from '../../../../../../queries/use-generic-query';
import { QUERY_KEYS } from '../../../../../../lib/query-constants';
import CommonCard from '../../../../../global/card';
import CommonTable from '../../../../../global/table';
import { SendOutlined } from '@ant-design/icons';
import usePost from '../../../../../../services/usePost';
import { capitalizeFirstLetter, formatDateTime } from '../../../../../../utils/functions';
import SOURCES from '../../../../../../utils/constants';
import NoRecordFound from '../../../../../global/noRecordFound';
import CommonSkeletonLoader from '../../../../../global/loading';
import { useGenericMutation } from '../../../../../../queries/use-generic-mutation';
import { message } from 'antd';
import CommonCardView from '../../../../../global/cardView';
import EnhancedLeadNotSendDetailsCardView from './enhancedLeadNotSendDetailsGridView';
import CommonDialog from '../../../../../global/dialog';
import { useQueryClient } from '@tanstack/react-query';

const EnhancedLeadNotSentDetails = () => {
    const { search } = useLocation();
    const queryParams = new URLSearchParams(search);
    const leadId = queryParams.get('leadId');
    const [selectedDestination, setSelectedDestination] = useState(null);
    const [confirmLeadSend, setConfirmLeadSend] = useState(false);
    const [leadsNotSentDetailsViewType, setLeadsNotSentDetailsViewType] = useState(() => {
        return localStorage.getItem("leadsNotSentDetailsViewType") || "GRID";
    });
    const navigate = useHistory();

    const queryClient = useQueryClient();
    const sendLeadsMutation = useGenericMutation({
        endpoint: "admin/lead/resend-to-destinations",
        onSuccess: () => {
            message.success("Leads send successfully.")
            queryClient.removeQueries({
                queryKey: QUERY_KEYS.leadsNotSent.lists({ leadId }),
                type: "all"
            })
        },
        onError: (err) => {
            console.log(err)
            message.error(err.response.error || "Error sending leads.")
        }
    })

    const { data, isLoading, isFetching } = useGenericQuery({
        endpoint: "leadstatistic/leads-not-sent",
        queryKey: QUERY_KEYS.leadsNotSent.lists({ leadId }),
        onSuccess: () => { },
        onError: () => { },
    })

    const { data: destinations, isLoading: destinationsLoading } = useGenericQuery({
        endpoint: "destinations/all-destinations",
        queryKey: QUERY_KEYS.destinations.all,
        onSuccess: () => { },
        onError: () => { },
    })

    const leadData = useMemo(() => {
        if (!Array.isArray(data) || !leadId) return [];

        return data
            // 1️⃣ filter by user
            .filter(item => item?.user?.userId === leadId)

            // 2️⃣ filter nested leads
            .map(item => {
                const filteredLeads =
                    selectedDestination === "all" || !selectedDestination
                        ? item.leads
                        : item.leads?.filter(
                            l => l.destination === selectedDestination
                        );

                return {
                    ...item,
                    leads: filteredLeads,
                };
            })

            // 3️⃣ keep only items with leads
            .filter(item => Array.isArray(item.leads) && item.leads.length > 0);

    }, [data, leadId, selectedDestination]);


    const sendLeads = async () => {
        if (!leadData || leadData.length === 0) return;

        const userEmail = leadData[0]?.userEmail || leadData[0]?.email;
        const leadIDs = leadData.map((lead) => lead.id || lead._id);

        const res = await sendLeadsMutation.mutateAsync({
            userEmail,
            leads: leadIDs,
            destination: selectedDestination
        });
        if (res.status === SOURCES.HTTP_CODE_200) {
            navigate.push('/admin/leads-not-send');
        }
    };

    const columns = [
        {
            title: "Name",
            dataIndex: "fullName",
            key: "fullName",
            render: (text, record) => (
                <span>{record.fullName ? record.fullName : `${record.firstName} ${record.lastName}`}</span>
            ),
            isAvatar: true
        },
        {
            title: "Phone",
            dataIndex: "phone",
            key: "phone",
        },
        {
            title: "Email",
            dataIndex: "email",
            key: "email",
        },
        {
            title: "Destination to Send",
            dataIndex: "destination",
            key: "destination",
            render: (text) => capitalizeFirstLetter(text)
        },
        {
            title: "Imported At",
            dataIndex: "importedAt",
            key: "importedAt",
            render: (text) => formatDateTime(text)
        }
    ];

    const tableData = useMemo(() => {
        if (!Array.isArray(leadData)) return [];

        return leadData.flatMap((lead) =>
            (lead.leads || []).map((ld) => ({
                ...ld,
                destination: ld.destination,
                importedAt: ld.importedAt,
                fullName: ld.fullName,
                firstName: ld.firstName,
                lastName: ld.lastName,
                phone: ld.phone,
                email: ld.email,
            }))
        );
    }, [leadData]);

    return (
        <CommonCard
            isExtraButton={{
                show: true,
                data: [
                    {
                        title: "Send Leads",
                        className: "flex items-center gap-x-1 !bg-primary text-primary hover:!text-white hover:!bg-primary/80 h-10 !bg-transparent !border !border-primary",
                        icon: <SendOutlined />,
                        onClick: () => {
                            if (selectedDestination && selectedDestination !== 'all' && tableData?.length > 0) {
                                setConfirmLeadSend(true)
                            }
                        },
                        disabled: !selectedDestination || selectedDestination === 'all' || tableData?.length === 0
                    }
                ]
            }}
            isFilters={{
                show: true,
                data: [{
                    name: "destinations",
                    placeholder: "Select Destination",
                    options: [{ value: "all", label: "All" }, ...Array.isArray(destinations) && destinations?.length > 0 ? destinations?.map((destination) => ({
                        value: destination?.destination,
                        label: destination?.name
                    })) : []],
                    onChange: (value) => setSelectedDestination(value),
                    className: "w-72"
                }]
            }}
            isListGridView={{
                show: true,
                setViewType: setLeadsNotSentDetailsViewType,
                viewType: leadsNotSentDetailsViewType,
                viewFor: "LeadsNotSendDetails",
            }}
            isLoading={{
                show: isLoading,
                loaderCmp: (
                    <CommonSkeletonLoader
                        rows={9}
                        type={leadsNotSentDetailsViewType === "GRID" ? "card" : "table"}
                        tableLength={5}
                    />
                ),
            }}
        >
            {Array.isArray(tableData) && tableData?.length > 0 ? (
                <>
                    {leadsNotSentDetailsViewType === "LIST" ? (
                        <CommonTable
                            columns={columns}
                            data={tableData}
                            loading={isLoading || isFetching}
                        />
                    ) : (
                        <CommonCardView>
                            {tableData.map((record, index) => (
                                <EnhancedLeadNotSendDetailsCardView
                                    key={record.id || index}
                                    record={record}
                                />
                            ))}
                        </CommonCardView>
                    )}
                </>
            ) : (
                <NoRecordFound message="No leads found." />
            )}

            {
                confirmLeadSend && (
                    <CommonDialog
                        title="Resend Leads"
                        description="Are you sure you want to resend these Leads?"
                        open={confirmLeadSend}
                        setOpen={() => setConfirmLeadSend(false)}
                        onOk={sendLeads}
                        onCancel={() => setConfirmLeadSend(false)}
                        isConfirmButtonLoading={sendLeadsMutation.isPending}
                    />
                )
            }
        </CommonCard>
    )
}

export default EnhancedLeadNotSentDetails